import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { BreadCrumbStateService } from '@go-modules/services/breadcrumb-state/breadcrumb-state.service';
import { MenuComponent } from '@go-modules/components/menus/menu/menu.component';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { ENVIRONMENTS, LTI_ENVIRONMENT_MODES } from '@go-modules/services/environment-vars/environments';
import { EventService } from '@go-modules/services/event/event.service';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { BadgeColors } from '@go-modules/components/badge/badge.constants';
import { NgxTaskService } from '@go-modules/services/task/task.service';
import { EMPTY, Observable, of, Subject } from 'rxjs';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { catchError, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { RouterService, ROUTE_NAMES } from '@go-modules/services/router/router.service';
import { GoPulseTourService } from '@go-modules/services/go-pulse-tour/go-pulse-tour.service';
import { ACTIVITY_TASK_INSTRUCTOR_TOGGLE_TOUR_CONFIG } from '@go-modules/services/go-pulse-tour/constants/activity-task-tour-instructor';
import { ResponsiveLayoutService } from '@go-modules/services/responsive-layout/responsive-layout.service';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';

@Component({
	selector: 'folder-breadcrumbs',
	templateUrl: './folder-breadcrumbs.component.html',
	styleUrls: ['./folder-breadcrumbs.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class FolderBreadCrumbsComponent implements OnInit, OnDestroy {
	public BadgeColors = BadgeColors;
	public environmentVarsService: EnvironmentVarsService;
	public isLti = false;
	public isCourseLevelLti = false;

	private readonly orgLevelStates = [ROUTE_NAMES.DASHBOARD_FOLDERS];
	private readonly folderLevelStates = [ROUTE_NAMES.DASHBOARD_FOLDER_VIEW];
	private readonly activityLevelStates = [
		ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW,
		ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK,
		ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK_REPORT,
		ROUTE_NAMES.DASHBOARD_ACTIVITY_REPORT
	];
	private readonly sessionLevelStates = [ROUTE_NAMES.DASHBOARD_SESSION_VIEW];
	public isTogglingActivityTasks = false;
	public isMobile$ = this.responsiveLayoutService.isMobileSize$;
	private componentDestroyed$$ = new Subject<void>();

	constructor (
		private routerService: RouterService,
		@Inject(selectedServiceToken) public selectedService: SelectedService,
		@Inject(userServiceToken) public userService: UserService,
		public breadcrumbStateService: BreadCrumbStateService,
		private eventService: EventService,
		private ngxTaskService: NgxTaskService,
		private ngxActivityService: NgxActivityService,
		private pulseTourService: GoPulseTourService,
		private featureFlag: NgxFeatureFlagService,
		private readonly responsiveLayoutService: ResponsiveLayoutService
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();

		const environment: any = this.environmentVarsService.get(EnvironmentVarsService.VAR.ENVIRONMENT) || {};
		this.isLti = this.environmentVarsService.environmentIs(ENVIRONMENTS.LTI);
		this.isCourseLevelLti = environment.mode === LTI_ENVIRONMENT_MODES.COURSE_LEVEL;
	}

	public ngOnInit (): void {
		this.breadcrumbStateService.navState$.pipe(
			distinctUntilChanged(),
			filter((navState) => navState === ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW),
			takeUntil(this.componentDestroyed$$)
		).subscribe(() => this.startActivityTaskToggleTour());
	}

	public ngOnDestroy (): void {
		this.componentDestroyed$$.next();
		this.componentDestroyed$$.complete();
	}

	private startActivityTaskToggleTour (): void {
		const activity = this.selectedService.getActivity();

		if (!activity ||
			this.isActivityPreviewEditorDialogEnabled() ||
			activity.show_tasks ||
			activity.parent_child_sync ||
			!this.selectedService.getGroup()?.hasInstructorRole(true)) {
			return;
		}

		this.pulseTourService.startTour(ACTIVITY_TASK_INSTRUCTOR_TOGGLE_TOUR_CONFIG).pipe(
			catchError(() => EMPTY)
		).subscribe();
	}

	private isOrgLevel (navState: ROUTE_NAMES): boolean {
		return this.orgLevelStates.includes(navState);
	}

	private isFolderLevel (navState: ROUTE_NAMES): boolean {
		return this.folderLevelStates.includes(navState);
	}

	private isActivityLevel (navState: ROUTE_NAMES): boolean {
		return this.activityLevelStates.includes(navState);
	}

	private isSessionLevel (navState: ROUTE_NAMES): boolean {
		return this.sessionLevelStates.includes(navState);
	}

	private getNavigationLevel (navState: ROUTE_NAMES): number {
		if (this.isOrgLevel(navState)) return 0;
		if (this.isFolderLevel(navState)) return 1;
		if (this.isActivityLevel(navState)) return 2;
		if (this.isSessionLevel(navState)) return 3;
		return -1;
	}

	public isFolderLevelOrDeeper (navState: ROUTE_NAMES): boolean {
		return this.getNavigationLevel(navState) >= 1;
	}

	public isActivityLevelOrDeeper (navState: ROUTE_NAMES): boolean {
		return this.getNavigationLevel(navState) >= 2;
	}

	public isAtSessionLevel (navState: ROUTE_NAMES): boolean {
		return this.isSessionLevel(navState);
	}

	public navigateToActivityView () {
		if (this.routerService.isRouteActive(ROUTE_NAMES.DASHBOARD_SESSION_VIEW)) {
			return this.eventService.broadcast(EVENT_NAMES.FEEDBACK_SESSION_REQUEST_EXIT, {
				exitData: null
			});
		}

		this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW, {
			activity_id: this.selectedService.getActivity().activity_id
		});
	}

	public navigateToFolderView () {
		if (this.routerService.isRouteActive(ROUTE_NAMES.DASHBOARD_SESSION_VIEW)) {
			this.eventService.broadcast(EVENT_NAMES.FEEDBACK_SESSION_REQUEST_EXIT, {
				exitData: null,
				callback: () => {
					this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_FOLDER_VIEW, {
						folder_id: this.selectedService.getGroup().group_id
					});
				}
			});
			return;
		}

		this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_FOLDER_VIEW, {
			folder_id: this.selectedService.getGroup().group_id
		});
	}

	public navigateToFoldersView () {
		if (this.routerService.isRouteActive(ROUTE_NAMES.DASHBOARD_SESSION_VIEW)) {
			this.eventService.broadcast(EVENT_NAMES.FEEDBACK_SESSION_REQUEST_EXIT, {
				exitData: null,
				callback: () => {
					this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_FOLDERS, {
						id: this.selectedService.getOrg().group_id
					});
				}
			});
			return;
		}

		this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_FOLDERS, {
			id: this.selectedService.getOrg().group_id
		});
	}

	public openMenu (event: MouseEvent, menu: MenuComponent) {
		// open browser menu on shift right click
		if (event.button === 2 && event.shiftKey) {
			return;
		}
		event.stopPropagation();
		event.preventDefault();
		menu.openMenu(event, event.target as HTMLElement);
	}

	public isRootUser (): boolean {
		return this.userService.currentUser?.is_root_user;
	}

	public canOpenOrgMenu (navState: ROUTE_NAMES, contextMenu: boolean = false): boolean {
		return (contextMenu || this.isOrgLevel(navState)) &&
			(this.isRootUser() || this.selectedService.getOrg().hasAdminRole(true));
	}

	public canOpenFolderMenu (navState: ROUTE_NAMES, contextMenu: boolean = false): boolean {
		return (contextMenu || this.isFolderLevel(navState))
			&& this.canOpenMenu()
			&& !this.environmentVarsService.environmentIs(ENVIRONMENTS.LTI);
	}

	public canOpenActivityMenu (navState: ROUTE_NAMES, contextMenu: boolean = false): boolean {
		return (contextMenu || this.isActivityLevel(navState)) &&
			this.canOpenMenu();
	}

	public canOpenMenu (): boolean {
		return this.selectedService.getGroup()?.hasInstructorRole(true) || this.isRootUser();
	}

	public showActivityTaskToggle (navState: ROUTE_NAMES): boolean {
		const activity = this.selectedService.getActivity();
		return this.isActivityLevel(navState) &&
				!this.isActivityPreviewEditorDialogEnabled() &&
				!activity.parent_child_sync &&
				this.selectedService.getGroup()?.hasInstructorRole(true);
	}

	private isActivityPreviewEditorDialogEnabled (): boolean {
		return this.featureFlag.isAvailable('ACTIVITY_PREVIEW_EDITOR_DIALOG');
	}

	public shouldShowCompleteBadge (navState: ROUTE_NAMES): Observable<boolean> {
		if (
			!this.isActivityLevel(navState) ||
			!this.selectedService.getGroup()?.hasPresenterRole() ||
			!this.ngxTaskService.shouldShowTasks()
		) {
			return of(false);
		}
		return this.ngxTaskService.allTasksComplete(this.selectedService.getActivity().activity_id);
	}

	public canManageActivityTasks (): boolean {
		const activity = this.selectedService.getActivity();
		if (!activity) {
			return false;
		}

		return this.canOpenMenu() && this.ngxActivityService.canEditActivity(activity);
	}

	public isBreadcrumbActive (navState: ROUTE_NAMES, level: number): boolean {
		return this.getNavigationLevel(navState) === level;
	}
}
